"use client";

import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FaBell, FaArrowUp, FaArrowDown,
  FaHandHoldingUsd, FaUsers, FaMoneyBillWave,
  FaCheckCircle, FaBolt
} from 'react-icons/fa';
import styles from './HeroVisualDemo.module.css';

const HeroVisualDemo = ({ heroInView }) => {
  const [showProfile, setShowProfile] = useState(false);
  const demoHomeRef         = useRef(null);
  const demoTransactionsRef = useRef(null);

  return (
    <motion.div
      className={styles.heroVisual}
      initial={{ opacity: 0, x: 40 }}
      animate={heroInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
    >

      {/* ── Floating badge: Rupayya score (top-left of phone) ── */}
      <motion.div
        className={`${styles.floatingBadge} ${styles.score}`}
        initial={{ opacity: 0, x: -20 }}
        animate={heroInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.9 }}
      >
        <div className={`${styles.badgeIcon} ${styles.green}`}>
          <FaCheckCircle color="white" size={13} />
        </div>
        <div className={styles.badgeText}>
          <span className={styles.badgeValue}>526 Score</span>
          <span className={styles.badgeLabel}>Excellent rating</span>
        </div>
      </motion.div>

      {/* ── Floating badge: transaction (bottom-left of phone) ── */}
      <motion.div
        className={`${styles.floatingBadge} ${styles.txn}`}
        initial={{ opacity: 0, x: -20 }}
        animate={heroInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.6, delay: 1.1 }}
      >
        <div className={`${styles.badgeIcon} ${styles.teal}`}>
          <FaBolt color="white" size={12} />
        </div>
        <div className={styles.badgeText}>
          <span className={styles.badgeValue}>₹2,000 sent</span>
          <span className={styles.badgeLabel}>Instantly settled</span>
        </div>
      </motion.div>

      {/* ── Phone shell ── */}
      <div className={styles.phoneMockup}>

        {/* Dynamic Island */}
        <div className={styles.iphoneNotch} />

        {/* Status bar */}
        <div className={styles.iphoneStatusBar}>
          <div className={styles.statusTime}>9:41</div>
          <div className={styles.statusIcons}>
            <div className={styles.signalIcon} />
            <div className={styles.wifiIcon} />
            <div className={styles.batteryIcon} />
          </div>
        </div>

        {/* Screen */}
        <div className={styles.phoneScreen}>
          <div className={styles.demoApp}>

            {/* Header */}
            <div className={styles.demoHeader}>
              <div className={styles.appLogo}>
                <img
                  src="/Assets/logo.png"
                  alt="Rupayya"
                  className={styles.logoImage}
                />
              </div>
              <div className={styles.demoNotifications}>
                <FaBell />
                <span className={styles.notificationBadge}>3</span>
              </div>
            </div>

            {/* Main content */}
            <div className={styles.demoMainContent}>

              {/* Score + stats */}
              <div ref={demoHomeRef} className={styles.demoHome}>
                <div className={styles.statsLayoutCompact}>

                  {/* Score card */}
                  <div className={styles.scoreCircleCompact}>
                    <div className={styles.scoreProgress}>
                      <div className={styles.scoreCircleContainer}>
                        <div className={styles.scoreContentInside}>
                          <div className={styles.scoreMainInside}>
                            <span className={styles.scoreValueInside}>526</span>
                            <span className={styles.scoreTotalInside}>/ 600</span>
                          </div>
                          <div className={styles.scoreLabelInside}>Rupayya Score</div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.scoreBadgeInside}>Excellent</div>

                    {/* Three stats inside card */}
                    <div className={styles.threeStatsTight}>
                      <div className={styles.statCardTight}>
                        <div className={styles.statNumberTight}>42</div>
                        <div className={styles.statLabelTight}>Network Size</div>
                      </div>
                      <div className={styles.statCardTight}>
                        <div className={styles.statNumberTight}>156</div>
                        <div className={styles.statLabelTight}>Transactions</div>
                      </div>
                      <div className={styles.statCardTight}>
                        <div className={styles.statNumberTight}>95%</div>
                        <div className={styles.statLabelTight}>Reputation</div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* App icons */}
                <div className={styles.appIconsSection}>
                  <div className={styles.sectionTitleRow}>
                    <h4 className={styles.iconsSectionTitle}>Quick Access</h4>
                    <span className={styles.viewAllBtn}>Edit</span>
                  </div>
                  <div className={styles.appIconsGrid}>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><FaHandHoldingUsd className={styles.iconImg} /></div>
                      <span className={styles.iconLabel}>F2F Lending</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><FaUsers className={styles.iconImg} /></div>
                      <span className={styles.iconLabel}>Community Pod</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><FaMoneyBillWave className={styles.iconImg} /></div>
                      <span className={styles.iconLabel}>Group Funds</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><div className={styles.iconEmoji}>📄</div></div>
                      <span className={styles.iconLabel}>Smart Bill</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><div className={styles.iconEmoji}>🏆</div></div>
                      <span className={styles.iconLabel}>Challenges</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><div className={styles.iconEmoji}>🤝</div></div>
                      <span className={styles.iconLabel}>P2P Lending</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><div className={styles.iconEmoji}>💳</div></div>
                      <span className={styles.iconLabel}>Bill Payments</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><div className={styles.iconEmoji}>🛡️</div></div>
                      <span className={styles.iconLabel}>Insurance</span>
                    </div>
                    <div className={styles.appIconItem}>
                      <div className={styles.iconCircle}><div className={styles.iconEmoji}>🧮</div></div>
                      <span className={styles.iconLabel}>Loan Calculator</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transactions */}
              <div ref={demoTransactionsRef} className={styles.demoTransactions}>
                <div className={styles.sectionTitleRow}>
                  <h4 className={styles.recentActivityTitle}>Recent Activity</h4>
                  <span className={styles.viewAllBtn}>View All</span>
                </div>
                <div className={styles.transactionsContainer}>
                  {[
                    { name: 'Rajesh', date: '2 days ago', amount: '-₹2,000', type: 'lent',     status: 'pending' },
                    { name: 'Priya',  date: '1 week ago', amount: '+₹1,500', type: 'borrowed', status: 'paid' },
                    { name: 'Amit',   date: '2 weeks ago',amount: '-₹5,000', type: 'lent',     status: 'pending' },
                    { name: 'Suresh', date: '3 days ago', amount: '+₹3,000', type: 'borrowed', status: 'paid' },
                    { name: 'Neha',   date: '1 day ago',  amount: '-₹1,500', type: 'lent',     status: 'pending' },
                    { name: 'Rahul',  date: '4 days ago', amount: '+₹2,500', type: 'borrowed', status: 'paid' },
                    { name: 'Meera',  date: '5 days ago', amount: '-₹3,500', type: 'lent',     status: 'pending' },
                  ].map((tx, i) => (
                    <div key={i} className={styles.transactionItem}>
                      <div className={styles.transactionInfo}>
                        <div className={`${styles.transactionIcon} ${styles[tx.type]}`}>
                          {tx.type === 'lent' ? <FaArrowUp /> : <FaArrowDown />}
                        </div>
                        <div>
                          <strong>{tx.name}</strong>
                          <span className={styles.transactionDate}>{tx.date}</span>
                        </div>
                      </div>
                      <div className={styles.transactionAmount}>
                        <span className={`${styles.amount} ${styles[tx.type]}`}>{tx.amount}</span>
                        <span className={`${styles.status} ${styles[tx.status]}`}>{tx.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Profile slide */}
              <div className={`${styles.demoProfileScreen} ${showProfile ? styles.show : ''}`}>
                <div className={styles.profileHeader}>
                  <button className={styles.profileBackBtn} onClick={() => setShowProfile(false)}>←</button>
                  <h3>Profile</h3>
                </div>
                <div className={styles.profileContent}>
                  <div className={styles.profileAvatar}>👤</div>
                  <h4>Rajmani</h4>
                  <p>Rupayya User</p>
                  <div className={styles.profileStats}>
                    <div><strong>526</strong><span>Score</span></div>
                    <div><strong>42</strong><span>Network</span></div>
                    <div><strong>₹1.2L</strong><span>Lent</span></div>
                  </div>
                </div>
              </div>

            </div>

            <div className={styles.iphoneHomeIndicator} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroVisualDemo;
